import { has, isNumber, find, findIndex, uniqBy, flow, partialRight, forOwn, findKey } from 'lodash'
import deepFreeze from 'deep-freeze'

import config from 'util/config.js'
import { emptyPutter } from 'models/Putter'
import { fromPutter } from 'models/Instance'
import { getAllPaintColors, getValidPaintColors, getSelected, getPaintFillLocationById, getPaintFillLocationByParameterKey, getSelectedColorByLocation, getDefaultColorByLocation} from './customize/selectors'
import { isPaintfillArea, getDefaultColors, getDefaultLocationsForColor, getPaletteColors } from './viewModels/helpers'
import { reshaft } from './customize/selectors/isValid'

function freeze(o) {
  return (!config.production) ? deepFreeze(o) : o;
}

//
// State
//
export const initialState = freeze({
  loading: true,
  putter: emptyPutter(),
  instance: fromPutter(emptyPutter()),
  replacementPaintFill: null,
  variableOptions: {
    flangeLineToe: false,
    flangeLineHeel: false,
    flangeLineCenter: false,
    topLineToe: false,
    topLineHeel: false,
    topLineCenter: false,
    InitialsFace: false,
    InitialsBumperHeel: false,
    InitialsBumperToe: false,
    frontCavityStamp: false,
    rearCavityStamp: false,
    toeStamp: false,
    InitialsArch: false
  }
});

export const colorKeyLookup = {
  InitialsFace: 'faceTextColor',
  InitialsBumperHeel: 'heelTextColor',
  InitialsBumperToe: 'toeTextColor',
  InitialsArch: 'archTextColor',
  rearCavityStamp: 'rearCavityStampColor',
  frontCavityStamp: 'frontCavityStampColor',
  toeStamp: 'toeStampColor',
  flangeLineToe: 'flangeLineToeColor',
  flangeLineHeel: 'flangeLineHeelColor',
  flangeLineCenter: 'flangeLineCenterColor',
  topLineCenter: 'topLineCenterColor',
  topLineToe: 'topLineToeColor',
  topLineHeel: 'topLineHeelColor',
  topSightDotCenter: 'topLineCenterColor',
  dotToe: 'dotToeColor',
  dotHeel: 'dotHeelColor',
  dotCenter: 'dotCenterColor',
  topLinePerpendicular: 'topLinePerpendicularColor',
  weightEngravingHeel: 'weightRearColor',
  weightEngravingToe: 'weightFrontColor',
  topWeightEngravingHeel: 'topWeightRearColor',
  topWeightEngravingToe: 'topWeightFrontColor',
}

//
// Modification functions 
// 
export function setPutter(state, putter) {
  return freeze(Object.assign({}, state, { putter }));
};
export function setInstance(state, instance) {
  return freeze(Object.assign({}, state, { instance }));
};
export function setVariableOption(state, field) {
  if (!has(state.variableOptions, field)) {
    throw new Error(`Trying to set invalid variable option '${field} '`);
  };
  const variableOptions = Object.assign({}, state.variableOptions, { [field]: true });
  return freeze(Object.assign({}, state, { variableOptions }));
}

export function setDefaultPaintFills(state){
  if(!!state.defaultInstance && !!state.defaultInstance.paintFillLocations)
  {
	state = clearPaletteColors(state);  
    let paintFillsToIgnore = [];
    state.defaultInstance.paintFillLocations.filter(paintFillLocation => { 
      let loc = getPaintFillLocationById(state, paintFillLocation.locationId)
      if (!loc) { paintFillsToIgnore.push(paintFillLocation.locationId); }
      return loc && !isPaintfillArea(loc.parameterKey)
    }).forEach( (paintFillLocation) => {
      let paintFillLocationParam = getPaintFillLocationById(state, paintFillLocation.locationId);
      if(!!paintFillLocationParam //&& (!isPaintfillArea(paintFillLocationParam.parameterKey)
        &&
        (
          ( !state.putter.faceSoleImageParameters.includes(paintFillLocationParam.parameterKey)
          || !state.putter.backCavityImageParameters.includes(paintFillLocationParam.parameterKey)
          || !state.putter.playerProfileImageParameters.includes(paintFillLocationParam.parameterKey) 
          )
          && !state.putter.existingFlangeLineParameters.includes(findKey(colorKeyLookup, colorKeyItem =>  colorKeyItem === paintFillLocationParam.parameterKey))
          && !state.putter.existingTopLineParameters.includes(findKey(colorKeyLookup, colorKeyItem =>  colorKeyItem === paintFillLocationParam.parameterKey))
          && !['topLinePerpendicular'].includes(findKey(colorKeyLookup, colorKeyItem =>  colorKeyItem === paintFillLocationParam.parameterKey)))
        )
      {
        paintFillsToIgnore.push(paintFillLocationParam.id);
      }
    });
    /*
    //Ignore stampSelections?
    state.instance.stampSelections.forEach( (stampSelection) => {
      console.log(getPaintFillLocationById(state, stampSelection.locationId));
      //paintFillsToIgnore.push(stampSelection.locationId);
    });
    */
    state.defaultInstance.paintFillLocations.filter(paintFillLocation => 
      !paintFillsToIgnore.includes(paintFillLocation.locationId) )
      .forEach(paintFillLocation => {
        try{ 
          state = selectPaint(state, paintFillLocation.locationId, paintFillLocation.colorId);
        }catch (e){
          if (e.name !== 'MaxColorsError') { throw (e); }
        }
      });

  }
  return state;
}

function setDefaultSelectionForPaintKey(state, paintKey){
  const colorKey = colorKeyLookup[paintKey];
  const loc = getPaintFillLocationByParameterKey(state, colorKey)
  const defaultInstancePaintFillColorId = getDefaultColorByLocation(state, loc.id).colorId;
  const selectedColorId = getSelectedColorByLocation(state, loc.id).colorId;
  if(loc && selectedColorId <= 0)
  {
    try{ 
      state = selectPaint(state, loc.id, defaultInstancePaintFillColorId) 
    }catch (e){
      if (e.name !== 'MaxColorsError') { throw (e); }
    }
  }
  return state;
}

function setDefaultSelectionPaintFill(state, field){ //eslint-disable-line no-unused-vars
  const sel = getSelected(state, field);
  const keys = (sel && sel.parameterKeys && sel.parameterKeys.split(',')) || [];
  return keys.reduce(setDefaultSelectionForPaintKey, state)
}

function clearSelectionForPaintKey(state, paintKey){
  const colorKey = colorKeyLookup[paintKey] || paintKey;
  const loc = getPaintFillLocationByParameterKey(state, colorKey);
  return (loc) ? selectPaint(state, loc.id, 0) : state; 
}

function clearSelectionPaintFill(state, field){
  const sel = getSelected(state, field);
  const keys = (sel && sel.parameterKeys && sel.parameterKeys.split(',')) || [];
  return keys.reduce(clearSelectionForPaintKey, state)
}

export function clearPaintFills(state) {
  let paintFillLocations = getSelected(state, 'paintFillLocations');
  
  //Here we grab the optional paintFills and create an array of their IDs to exclude when we clear paintFills
  let paintFillsToKeep = [];
  const initials = getSelected(state, 'initials');
  forOwn(initials, function(value, key){
    if(value.enabled)
      paintFillsToKeep.push(getPaintFillLocationByParameterKey(state, colorKeyLookup[key]).id);
  });
  const stampSelections = getSelected(state, 'stampSelections');
  stampSelections.forEach(stampSelection => { 
    if(stampSelection.enabled)
    {
      paintFillsToKeep.push(getPaintFillLocationByParameterKey(state, stampSelection.location.paintFillKey).id)
    } 
  });
  const sightDot = getSelected(state, 'sightDot');
  if(sightDot && sightDot.paintFillKeys)
  {
    let perpendicularLine = find(state.putter.paintFillLocation, { 'parameterKey' : 'topLinePerpendicularColor' });
    if(perpendicularLine)
    {
      //if putter has perpendicular line paintfill instead of sight dot paintfill, keep that instead
      paintFillsToKeep.push(perpendicularLine.id);
    }
    else
    {
      sightDot.paintFillKeys.split(',').forEach(paintFillKey => 
        paintFillsToKeep.push(getPaintFillLocationByParameterKey(state, paintFillKey).id)
      );
    }
  }
  const topLines = getSelected(state, 'topLine');
  if(topLines && topLines.paintFillKeys)
  {
    let perpendicularLine = find(state.putter.paintFillLocation, { 'parameterKey' : 'topLinePerpendicularColor' });
    if(perpendicularLine)
    {
      //if putter has perpendicular line paintfill instead of top line paintfill, keep that instead 
      paintFillsToKeep.push(perpendicularLine.id);
    }
    else
    {
      topLines.paintFillKeys.split(',').forEach(paintFillKey => 
        paintFillsToKeep.push(getPaintFillLocationByParameterKey(state, paintFillKey).id)
      );
    }
  }
  const flangeLines = getSelected(state, 'flangeLine');
  if(flangeLines && flangeLines.paintFillKeys)
  {
    flangeLines.paintFillKeys.split(',').forEach(paintFillKey => 
      paintFillsToKeep.push(getPaintFillLocationByParameterKey(state, paintFillKey).id)
    );
  }
  const weightEngravingHeel = getSelected(state, 'weightEngravingHeel');
  if(weightEngravingHeel)
  {
    paintFillsToKeep.push(getPaintFillLocationByParameterKey(state, 'weightRearColor').id)
  }
  const weightEngravingToe = getSelected(state, 'weightEngravingToe');
  if(weightEngravingToe)
  {
    paintFillsToKeep.push(getPaintFillLocationByParameterKey(state, 'weightFrontColor').id)
  }
  const weightEngravingTopHeel = getSelected(state, 'weightEngravingTopHeel');
  if(weightEngravingTopHeel)
  {
    paintFillsToKeep.push(getPaintFillLocationByParameterKey(state, 'topWeightRearColor').id)
  }
  const weightEngravingTopToe = getSelected(state, 'weightEngravingTopToe');
  if(weightEngravingTopToe)
  {
    paintFillsToKeep.push(getPaintFillLocationByParameterKey(state, 'topWeightFrontColor').id)
  }
  paintFillLocations = paintFillLocations.filter(paintFillLocation => paintFillsToKeep.includes(paintFillLocation.locationId))
  let selection = { paintFillLocations };
  
  const instance = Object.assign({}, state.instance, selection);
  let newstate = freeze(Object.assign({}, state, { instance }));
  return clearPaletteColors(newstate);
}

export function clearAllPaintFills(state) {
  let selection = { 'paintFillLocations': [], 'paletteColors': []};
  const instance = Object.assign({}, state.instance, selection);
  return freeze(Object.assign({}, state, { instance }));
}

export function clearPaletteColors(state) {
  let selection = { 'paletteColors': []};
  const instance = Object.assign({}, state.instance, selection);
  return freeze(Object.assign({}, state, { instance }));
}

function clearIdSelection(state, field) {
  if (!isNumber(state.instance[field])) {
    throw new Error(`Trying to set non-id field '${field}' to 0 in instance`);
  }
  let selection = {};
  selection = { [field]: 0}
  const instance = Object.assign({}, state.instance, selection);
  return freeze(Object.assign({}, state, { instance }));
}

export function select(state, field, id) {
  //ignore dummy 'refinish' option used for Finish & Paintfill 
  if (!has(state.instance, field) && field !== 'refinish') {
    throw new Error(`Trying to set invalid instance field '${field}'`);
  }
  if (!isNumber(state.instance[field]) && field !== 'refinish') {
    throw new Error(`Trying to set non-id field '${field}' in instance`);
  }

  let selection = {}
  switch (field) { 
    case 'topWeight' :
    case 'weight':
      selection = { [field]: id };
      state = setInstance(state, Object.assign({}, state.instance, selection));
      //check if a weight engraving is selected
      if(state.instance.weightEngravingHeel > 0)
      {
        let stamp = find(state.putter.weightEngravingStamp, { id : state.instance.weightEngravingHeel });
        //check if the selected weight engraving is disabled for this weight id
        if(stamp.disabledWeightIds.split(',').indexOf(id.toString()) > -1 )
        {
          //remove the weight engraving selection
          state = clearIdSelection(state, 'weightEngravingHeel');
        }
      }
      //check if a weight engraving is selected
      if(state.instance.weightEngravingToe > 0)
      {
        let stamp = find(state.putter.weightEngravingStamp, { id : state.instance.weightEngravingToe });
        //check if the selected weight engraving is disabled for this weight id
        if(stamp.disabledWeightIds.split(',').indexOf(id.toString()) > -1 )
        {
          //remove the weight engraving selection
          state = clearIdSelection(state, 'weightEngravingToe');
        }
      }
      break;

    case 'weightEngravingHeel':
    case 'weightEngravingToe':
    case 'weightEngravingTopToe':
    case 'weightEngravingTopHeel':
      if (id !== 0 && !find(state.putter.weightEngravingStamp, { id })) {
        throw new Error(`Trying to stamp invalid weight stamp id: ${id}`);
      }
      if(id === 0 && state.instance.paintFill === 7)
      {
        //if weight engraving is de-selected and paintfill is not enabled (keep as is), then de-select the paintFill
        state = clearSelectionForPaintKey(state, field);
      }
      selection = { [field]: id };
      break;

    case 'topLine':
    case 'sightDot': 
      state = clearSelectionPaintFill(state, field);
      selection = { [field]: id } 
      state = setInstance(state, Object.assign({}, state.instance, selection));
      
      const otherField = (field === 'topLine') ? 'sightDot' : 'topLine';
      const selected = getSelected(state, field);
      const otherFieldSelected = getSelected(state, otherField);
        
      // topLine and sightDots are mutually exclusive, but tracked separately
      if (id !== 0) {
        if(!!selected && !!otherFieldSelected && selected.parameterKeys !== otherFieldSelected.parameterKeys)
        {
          state = clearSelectionPaintFill(state, otherField);
        }
        selection[otherField] = 0; 
      }
      //Paintfill is keep as is (id 7) && the selected option has no lines aka 'keep as is' && the putter has a perpendicular line then clear the paintfill
      const selectedPaintFill = getSelected(state, 'paintFill');
      let perpendicularLine = find(state.putter.paintFillLocation, { 'parameterKey' : 'topLinePerpendicularColor' });
      if(selectedPaintFill.id === 7 && selected.numberOfLines === 0 && perpendicularLine)
      {
        state = clearSelectionForPaintKey(state, 'topLinePerpendicularColor');
      }
      break;

    case 'flangeLine':
      state = clearSelectionPaintFill(state, field);
      selection = { [field]: id } 
      state = setInstance(state, Object.assign({}, state.instance, selection));
      
      break; 
    
    case 'paintFill':
      let changingFinish = false;
	  let disableRestoration = false;
      const restorationType = getSelected(state, 'restorationType');
      if(restorationType)
      {
        //if restorationType was selected, clear that selection and clear accessory selections, also reset reshaft to 'keep shaft'
		disableRestoration = true;
        state = clearIdSelection(state, 'restorationType');
        state = clearIdSelection(state, 'grip');
        state = clearIdSelection(state, 'shaftBand');
        state = clearIdSelection(state, 'headCover');
		
		let selLength = getSelected(state, 'length');
		
		//clear the reshaft unless reshaft is required based on length
		if(selLength == null || !selLength.reshaftRequired) {
			const reshaftKeepShaft = find(state.putter.reshaft, (reshaft) => reshaft.keepShaft);
			if(reshaftKeepShaft)
			{
			  selection = { 'reshaft': reshaftKeepShaft.id };
			  state = setInstance(state, Object.assign({}, state.instance, selection));
			}
			else
			{
			   state = clearIdSelection(state, 'reshaft');
			}
		} else {
		console.log('selLength=' + selLength.reshaftRequired);
		
		console.log('obj=' + JSON.stringify(selLength));
		}
		
        
        if(state.defaultInstance && state.instance.finish !== state.defaultInstance.finish)
        {
          changingFinish = true;
        }
        else
        {
          state = clearIdSelection(state, 'finish');
        }
      }
      
      //if new paintFill, clear paintFills, unless already set to new paintFill
      if(id === 6 && (state.instance.paintFill !== 6 || disableRestoration))
      {
        if(state.defaultInstance && state.instance.finish !== state.defaultInstance.finish)
        {
          changingFinish = true;
        }
        //state = clearPaintFills(state);
        state = clearPaintFills(state);
      }
      //if keep as is, reset paintfills to blank and clear finish
      else if(id === 7)
      {
        //state = clearPaintFills(state);
        state = clearPaintFills(state);
        if(state.defaultInstance && state.defaultInstance.finish)
        {
          changingFinish = true;
        }
        else
        {
          state = clearIdSelection(state, 'finish');
        }
      }
      //Set finish to default instance finish
      if(changingFinish)  
      {
        selection = { 'finish': state.defaultInstance.finish };
        state = setInstance(state, Object.assign({}, state.instance, selection));
      }
      //if changing the finish then check the current paints are valid
      if(changingFinish)
        state = freeze(Object.assign({}, state, { 'paintsValid' : checkPaintsValid(state) }));
      else
      {
        //some default paints are not valid for black finish, so clear those
        state = clearInvalidPaints(state);
      }
      selection = { [field]: id };
      state = setInstance(state, Object.assign({}, state.instance, selection));
      
      break;

    case 'restorationType':
      //if "restoration" paintFill option is selected, then set paintFill to "new paintfill" (id 6), 
      // and reshaft to either the appropriately tagged option, or old hardcoded id "reshaft" (id:12)
      //also grip should default to 'replace to standard grip' 
      //headCover default to 'replace to standard headCover' 
      //shaftBand default to 'replace to standard shaftBand' 
      //if none chosen
      //var enablingPaint = (state.instance.paintFill !== 6);
	  var enablingPaint = true;
      selection = { 'paintFill': 6, 
        'reshaft': find(state.putter.reshaft, (reshaft) => reshaft.restorationDefault === true) ?
                find(state.putter.reshaft, (reshaft) => reshaft.restorationDefault === true).id : 12, 
        'grip': state.instance.grip ? state.instance.grip : find(state.putter.grip, (grip) => grip.restorationDefault === true) ?
                find(state.putter.grip, (grip) => grip.restorationDefault === true).id : 0, 
        'headCover':  state.instance.headCover ? state.instance.headCover : find(state.putter.headCover, (headCover) => headCover.restorationDefault === true) ?
                      find(state.putter.headCover, (headCover) => headCover.restorationDefault === true).id : 0,
        'shaftBand':  state.instance.shaftBand ? state.instance.shaftBand : find(state.putter.shaftBand, (shaftBand) => shaftBand.restorationDefault === true) ? 
                      find(state.putter.shaftBand, (shaftBand) => shaftBand.restorationDefault === true).id : 0,
      }
      //only set defaults if restoration picked for the first time, so re-clicking add restoration will not reset defaults
      if(state.instance.restorationType !== 14)
      {
        state = setInstance(state, Object.assign({}, state.instance, selection));
        if(enablingPaint)
        {
          //only set default paintFill if paintFill selection is changed
          state = setDefaultPaintFills(state);
          state = setInstance(state, Object.assign({}, state.instance, selection));
        }
        //Set finish to default instance finish
        if(state.defaultInstance && state.instance.finish !== state.defaultInstance.finish)
        {
          selection = { 'finish': state.defaultInstance.finish };
          state = setInstance(state, Object.assign({}, state.instance, selection));
        }
        if(!state.defaultInstance)
        {
          selection = { 'finish': 2 };
          state = setInstance(state, Object.assign({}, state.instance, selection));
        }
        //some default paints are not valid for black finish, so clear those
        state = clearInvalidPaints(state);
      }
      
      selection = { [field]: id };
      break;

    case 'desiredLength': // find and select a default weight if it exists
      selection = { [field]: id };
      let l = find(state.putter.desiredLength, (o) => o.id === id)
      if (l && l.defaultWeight) {
        selection['weight'] = l.defaultWeight
        if (state.putter.hasTopWeights){ 
          selection['topWeight'] = l.defaultWeight
        }
      }
      break;

    case 'length':
      selection = { [field]: id };
      state = setInstance(state, Object.assign({}, state.instance, selection));
      const selectedLength = getSelected(state, field);
      const selectedRestoration = getSelected(state, 'restorationType')
      
      if((selectedRestoration && selectedRestoration.id) || selectedLength.reshaftRequired) {
        //if reshaftRequired, set reshaft to restoration default'reshaft' (or fallback to id: 12)
          // and set to default grip and shaftBand if no default grip or shaftBand has been chosen

          let reshaftVal1 = find(state.putter.reshaft, (reshaft) => reshaft.restorationDefault === true);
          if (typeof reshaftVal1 !== 'undefined') {
              let reshaftVal2 = find(state.putter.reshaft, (reshaft) => reshaft.restorationDefault === true).id;
              console.log(reshaftVal1);
              console.log(reshaftVal2);
          }

          let reshaftDefaultId = 12;
          let reshaftId = 0;

          if (state.instance.reshaft !== 0 && state.instance.reshaft !== 13) {
              reshaftId = state.instance.reshaft;
          } else {
              reshaftId = find(state.putter.reshaft, (reshaft) => reshaft.restorationDefault === true) ?
                  find(state.putter.reshaft, (reshaft) => reshaft.restorationDefault === true).id : reshaftDefaultId;
          }

        selection = { 
          'reshaft': reshaftId, 
          'grip': state.instance.grip ? state.instance.grip : find(state.putter.grip, (grip) => grip.restorationDefault === true) ?
                  find(state.putter.grip, (grip) => grip.restorationDefault === true).id : 0, 
          'shaftBand': state.instance.shaftBand ? state.instance.shaftBand : find(state.putter.shaftBand, (shaftBand) => shaftBand.restorationDefault === true) ? 
                        find(state.putter.shaftBand, (shaftBand) => shaftBand.restorationDefault === true).id : 0,
        };
        state = setInstance(state, Object.assign({}, state.instance, selection));
      } else {
        //otherwise, reset shaft to first option available
        selection = { 'reshaft' : state.putter.reshaft[0].id };
        state = setInstance(state, Object.assign({}, state.instance, selection));
      }
      //if length option requires a user selected desired length then default to the first option
      if(selectedLength.userSelection && state.putter.desiredLength && state.putter.desiredLength.length > 0) {
        selection = { 'desiredLength' : state.putter.desiredLength[0].id };
        state = setInstance(state, Object.assign({}, state.instance, selection));
      }
      break;
    
    case 'reshaft': 
      var newReshaft = find(state.putter['reshaft'], { id });
      if (newReshaft && !newReshaft.keepShaft) {
        //grip should default to 'replace to standard grip'
        //shaftBand default to 'replace to standard shaftBand'
          //if none chosen

          let curShaftBand = state.instance.shaftBand;
          let newShaftBand = curShaftBand;

          //don't allow Shaft Rings for graphite shaft
          if (newReshaft.label.toLowerCase().includes("graphite")) {
              if (curShaftBand > 0) {
                  let shaftBandObj = state.putter.shaftBand.find(item => item.id === curShaftBand);
                  if (shaftBandObj && shaftBandObj.label.toLowerCase().includes("shaft ring")) {
                      newShaftBand = 0;
                  }
              }
          }

          //if they haven't selected a shaftBand, look for Restoration default
          newShaftBand = state.instance.shaftBand ? newShaftBand : find(state.putter.shaftBand, (shaftBand) => shaftBand.restorationDefault === true) ?
              find(state.putter.shaftBand, (shaftBand) => shaftBand.restorationDefault === true).id : 0;
        selection = {  [field]: id , 
          'grip': state.instance.grip ? state.instance.grip : find(state.putter.grip, (grip) => grip.restorationDefault === true) ?
                  find(state.putter.grip, (grip) => grip.restorationDefault === true).id : 0, 
          'shaftBand': newShaftBand
        }
      } else {
        selection = { [field] : id };
      }
      break;
    //For checking the paints are valid when you switch finishes which would cause invalid colors selected
    case 'finish':
      selection = { [field]: id };
      state = setInstance(state, Object.assign({}, state.instance, selection));
      state = freeze(Object.assign({}, state, { 'paintsValid' : checkPaintsValid(state) }));
      //state = clearInvalidPaints(state);
      break;
    default:
      //TODO: Check all selections
      selection = { [field]: id };
      break;
  }
  const instance = Object.assign({}, state.instance, selection);
  return freeze(Object.assign({}, state, { instance }));
};

export function applyFinish(state, apply) {
  if(apply)
  {
    state = clearInvalidPaints(state);
    return freeze(Object.assign({}, state, { 'paintsValid': true })); 
  }
  else
  {
    const selection = { 'finish': 2 };
    const instance = Object.assign({}, state.instance, selection);
    return freeze(Object.assign({}, state, { instance, 'paintsValid': true }));
  }
}

export function setValue(state, field, value){
  const selection = { [field]: value };
  const instance = Object.assign({}, state.instance, selection); 
  return freeze(Object.assign({}, state, { instance }));
}


export function selectInitials(state, locationName, field, value) {
  if (field === 'enabled' && !value){ 
    var defaultStyle = (state.putter.personalizationStyle.length) ? state.putter.personalizationStyle[0].id : 0;
    state = flow(
      partialRight(selectInitials, locationName, 'characters', ''),
      partialRight(selectInitials, locationName, 'personalizationStyleId', defaultStyle),
      partialRight(selectInitials, locationName, 'separateWithPeriods', false),
      partialRight(clearSelectionForPaintKey, locationName)
    )(state);
  }

  if (field === 'characters') { value = value.toUpperCase(); }

  const loc = state.instance.initials[locationName];
  const initials = Object.assign({}, state.instance.initials, {
    [locationName]: Object.assign({}, loc, { [field]: value })
  });
  const instance = Object.assign({}, state.instance, { initials })
  return freeze(Object.assign({}, state, { instance }));
};

export function selectStamp(state, locationId, field, value) {
  //disable initials code goes here
  const loc = find(state.putter.stampLocation, { id: locationId });
  if (!loc) {
    throw new Error(`Trying to stamp invalid location id: ${locationId}`);
  }

  const enabled = (field === 'enabled') ? value : (field === 'stampId' && !!value);
  const stampId = (field === 'stampId') ? value : null;
  let stampSelections = state.instance.stampSelections;
  const existing = findIndex(stampSelections, { locationId });

  if (stampId && !find(state.putter.stamp, { id: stampId })) {
    throw new Error(`Trying to stamp invalid stamp id: ${stampId}`);
  }
    
  if (existing > -1) {
    if (enabled) {
      stampSelections = [
        ...stampSelections.slice(0, existing),
        Object.assign({}, stampSelections[existing], { enabled, stampId }),
        ...stampSelections.slice(existing + 1)
      ];
    } else {
      stampSelections = [
        ...stampSelections.slice(0, existing),
        ...stampSelections.slice(existing + 1)
      ];
      if(!isPaintfillArea(loc.parameterKey) || state.instance.paintFill === 7)
      {
        state = clearSelectionForPaintKey(state, loc.parameterKey);
      }
    }

  } else if (enabled || stampId) {
    stampSelections = [...stampSelections, { enabled, locationId, stampId }];
  }

  const instance = Object.assign({}, state.instance, { stampSelections });
  return freeze(Object.assign({}, state, { instance }));
};

export function selectPaint(state, locationId, colorId) {
  if (!find(state.putter.paintFillLocation, { id: locationId })) {
    throw new Error(`Trying to paint invalid location id: ${locationId}`);
  }
  if (colorId && !find(getAllPaintColors(state), { id: colorId })) {
    throw new Error(`Trying to paint invalid color id: ${colorId}`);
  }

  let paintFillLocations = state.instance.paintFillLocations; 
  if (colorId) { 
    //don't count 'raw' color as a color in the max color limit
    const rawColors = getAllPaintColors(state).filter(color => color.isRaw);
    let paletteColors = getPaletteColors(state);
	const newColors = [{ id: colorId }, ...paletteColors];
	const numColors = uniqBy(newColors.filter(color => {
      if(rawColors.length > 0) 
        return rawColors.filter(rawColor => rawColor.id === color.id).length === 0
      else
        return true;
      }), 'id').length;
	  
    if (numColors > state.putter.maxPaintfillColors) {
      var e = new Error('Maximum color count reached');
      e.name = 'MaxColorsError';
      //return state;
      throw(e)
    }
  }

  const existing = findIndex(paintFillLocations, { locationId });
  if (existing > -1) {
    if (colorId) {
      paintFillLocations = [
        ...paintFillLocations.slice(0, existing),
        Object.assign({}, paintFillLocations[existing], { colorId }),
        ...paintFillLocations.slice(existing + 1)
      ];
    } else {
      paintFillLocations = [
        ...paintFillLocations.slice(0, existing),
        ...paintFillLocations.slice(existing + 1)
      ];
    } 
  } else if (colorId) {
    paintFillLocations = [...paintFillLocations, { locationId, colorId }];
  }
  
  state = selectPaletteColor(state, colorId, -1);

  const instance = Object.assign({}, state.instance, { paintFillLocations });
  return freeze(Object.assign({}, state, { instance }));
};

export function replacePaint(state, oldColorId, newColorId, additionalLocationId) { 
  let paletteColors = getPaletteColors(state);
  if(oldColorId < 0) {
	  let tempState = selectPaletteColor(state, newColorId, oldColorId);
	  return tempState;
  }


  let paintFillLocations = state.instance.paintFillLocations.filter(l =>
    l.colorId !== oldColorId || (l.colorId === oldColorId  && !!find(getValidPaintColors(state, l.locationId), 
    { id: 
        //if putter has 'raw cavity dot' paint color (onCavityDotsOnly === 'True') 
        //and paint fill location isCavityDot
        //and new color is raw 
        //replace newColorId with 'raw cavity dot' id
        (state.putter.paintFillColor.filter(color => color.onCavityDotsOnly === 'True').length > 0 
        && getPaintFillLocationById(state, l.locationId).isCavityDot 
        && state.putter.paintFillColor.filter(color => color.id === newColorId)[0].isRaw)
        ? state.putter.paintFillColor.filter(color => color.onCavityDotsOnly === 'True')[0].id
        : newColorId }))
  );
  paintFillLocations = paintFillLocations.map(l => {
    return (l.colorId === oldColorId) ?
      { locationId: l.locationId,
        colorId: 
          //if putter has 'raw cavity dot' paint color (onCavityDotsOnly === 'True') 
          //and paint fill location isCavityDot
          //and new color is raw 
          //replace newColorId with 'raw cavity dot' id
          (state.putter.paintFillColor.filter(color => color.onCavityDotsOnly === 'True').length > 0 
          && getPaintFillLocationById(state, l.locationId).isCavityDot 
          && state.putter.paintFillColor.filter(color => color.id === newColorId)[0].isRaw)
          ? state.putter.paintFillColor.filter(color => color.onCavityDotsOnly === 'True')[0].id
          : newColorId } :
      l;
  });
  const instance = Object.assign({}, state.instance, { paintFillLocations });
  let newState = freeze(Object.assign({}, state, { instance })); 

  newState = selectPaletteColor(newState, newColorId, oldColorId);

  if (additionalLocationId && additionalLocationId.length > 0)
  {
    additionalLocationId.forEach((locId) => newState = selectPaint(newState, locId, newColorId));
  }
    
  return newState;
};

/*
export function replacePaintByDefaultColorRank(state, colorRank, newColorId) { 
  let defaultColors = getDefaultColors(state);
    
  let colorIndex = colorRank + 4;
  let oldColorId = 0;
  if(defaultColors.length <= colorIndex) {
	  return state;
  }
  
  let newState = state;
  oldColorId = defaultColors[colorIndex];
  let defaultLocations = getDefaultLocationsForColor(state, oldColorId);
  if(defaultLocations.length <= 0) {
	  return state;
  }
  
  defaultLocations.forEach(function(locId) {
	  let found = state.instance.paintFillLocations.some((location) => {
		  return (location.locationId == locId);
	  });
	  
	  if(!found) {
		if (find(newState.putter.paintFillLocation, { id: locId })) {
			newState = selectPaint(newState, locId, newColorId);  
		}  
	  }
  });
  
  return newState;
};*/

export function addAccessory(state, id) {
  if (!find(state.putter.accessories, { id })) {
    throw new Error(`Trying to select invalid accessory: ${id}`);
  }
  const accessories = [...state.instance.accessories, id];
  const existing = state.instance.accessories.indexOf(id);
  if (existing >= 0) { return state; }
  const instance = Object.assign({}, state.instance, { accessories });
  return freeze(Object.assign({}, state, { instance }));
};

export function removeAccessory(state, id) {
  let accessories = state.instance.accessories;
  const existing = findIndex(accessories, id);
  if (existing > -1) {
    accessories = [
      ...accessories.slice(0, existing),
      ...accessories.slice(existing + 1)
    ];
  }
  const instance = Object.assign({}, state.instance, { accessories });
  return freeze(Object.assign({}, state, { instance }));
};

export function selectDefaultPaint(state, locationId) {
  if (!find(state.putter.paintFillLocation, { id: locationId })) {
    throw new Error(`Trying to paint invalid location id: ${locationId}`);
  }
  const defaultPaint = getDefaultColorByLocation(state, locationId);
  return selectPaint(state, locationId, defaultPaint.colorId);
}

export function checkPaintSwapValid(state, oldColor, newColor) {
  if(!oldColor || !newColor)
    return true;
  let paintFillLocationsForOldColor = getSelected(state, 'paintFillLocations').filter(
    paintFillLocation => paintFillLocation.colorId === oldColor.id
  );
  // for swapping to a raw color, skip the check on the cavity dot if raw is allowed on cavity dot since there are 2 raw colors
  if(newColor.isRaw && state.putter.paintFillColor.filter(color => color.onCavityDotsOnly === 'True').length > 0)
    paintFillLocationsForOldColor = paintFillLocationsForOldColor.filter(paintFillLocation => paintFillLocation.location.isCavityDot === false);
  let check = paintFillLocationsForOldColor.every(
    paintFillLocation => find(getValidPaintColors(state, paintFillLocation.locationId), { id: newColor.id })
  )
  return check;
}

//For checking the paints are valid when you switch finishes which would cause invalid colors selected
export function checkPaintsValid(state) {
  const paintFillLocations = getSelected(state, 'paintFillLocations');
  return paintFillLocations.every(
    paintFillLocation => find(getValidPaintColors(state, paintFillLocation.locationId), { id: paintFillLocation.colorId })
  )
}

export function clearInvalidPaints(state) {
  let paintFillLocations = getSelected(state, 'paintFillLocations');
  paintFillLocations = paintFillLocations.filter( paintFillLocation => 
    !!find(getValidPaintColors(state, paintFillLocation.locationId), { id: paintFillLocation.colorId })
  );
  let selection = { paintFillLocations };
  
  const instance = Object.assign({}, state.instance, selection);
  return freeze(Object.assign({}, state, { instance }));
}

export function selectPaletteColor(state, colorId, oldColorId) {
	let paletteColors = getPaletteColors(state);
	if(paletteColors == null) {
		paletteColors = [];
	}
	
	if (paletteColors.length >= state.putter.maxPaintfillColors && oldColorId < 0) {
		return state;
	}
	
	if(colorIsRaw(state, colorId)) {
		return state;
	}
	
	for(let y = 0; y < paletteColors.length; y++) {
		if(paletteColors[y].id == colorId) {
			return state;
		}
	}
	
	let allColors = getAllPaintColors(state);
	let foundColors = allColors.filter(color => color.id === colorId);
	if(!foundColors && foundColors.length == 0) {
		return state;
	}

	if (typeof foundColors[0] == "undefined" || foundColors[0] == null) {
		return state;
	}

	let replaceColor = null;
	if(oldColorId > -1) {
		let foundOldColors = allColors.filter(color => color.id === oldColorId);
		if(foundOldColors && foundOldColors.length > 0) {
			replaceColor = foundOldColors[0];
		}
	}
	
	let oldColorIndex = -1;
	if(replaceColor != null) {
		oldColorIndex = findIndex(paletteColors, (color) => { return color.id == oldColorId;});	
	}

	if(oldColorIndex > -1) {
		paletteColors = [...paletteColors.slice(0, oldColorIndex),
        foundColors[0],
        ...paletteColors.slice(oldColorIndex + 1)];
	} else {
		paletteColors = [...paletteColors, foundColors[0]];
	}

	const instance = Object.assign({}, state.instance, { paletteColors });
	return freeze(Object.assign({}, state, { instance }));
}

export function colorIsRaw(state, colorId) {
	const rawColors = getAllPaintColors(state).filter(color => color.isRaw);
	if(rawColors.length > 0) 
        return rawColors.filter(rawColor => rawColor.id === colorId).length > 0
      else
        return false;
} 

  // This stuff is how the old customizer determined which of the optional parameters are active given the users' choices

  // var dictionary = {
  //   InitialsFace: 'faceTextColor',
  //   InitialsBumperHeel: 'heelTextColor',
  //   InitialsBumperToe: 'toeTextColor',
  //   InitialsArch: 'archTextColor',
  //   rearCavityStamp: 'rearCavityStampColor',
  //   frontCavityStamp: 'frontCavityStampColor',
  //   toeStamp: 'toeStampColor',
  //   flangeLineToe: 'flangeLineToeColor',
  //   flangeLineHeel: 'flangeLineHeelColor',
  //   flangeLineCenter: 'flangeLineCenterColor',
  //   topLineCenter: 'topLineCenterColor',
  //   topLineToe: 'topLineToeColor',
  //   topLineHeel: 'topLineHeelColor'
  // }

  // initialsTest: function(){
  //   var initialTypes = ['InitialsFace', 'InitialsBumperHeel', 'InitialsBumperToe'];
  //   for (var i = 0; i < initialTypes.length; i += 1){
  //     var initialsData = this.get('Initials').get(initialTypes[i]).toJSON();
  //     // Note !! is just a typecast to a boolean, it'll be true when it's not an empty string.
  //     this.variableOptionsMap[initialTypes[i]] = !!initialsData.Characters;
  //     if (i === 0 && initialsData.Characters && initialsData.PersonalizationStyleId === 3){
  //       this.variableOptionsMap.InitialsFace = false;
  //       this.variableOptionsMap.InitialsArch = true;
  //     }
  //   }
  // },

  // stampTest: function(){
  //   var forbidden = ['toeStamp', 'rearCavityStamp', 'frontCavityStamp'];
  //   var stamps = this.get('StampSelections').toJSON();
  //   for(var i = 0; i < stamps.length; i += 1){
  //     // I'm allowed to just use [0] because the locationid should be a primary key
  //     var stampLoc = sandbox.stampLocations.where({id: stamps[i].LocationId})[0].toJSON();
  //     if(_.indexOf(forbidden, stampLoc.parameter_key) > -1){
  //       this.variableOptionsMap[stampLoc.parameter_key] = true;
  //     }
  //   }
  // },

  // lineDotTest: function(){
  //   // I'm 100% sure there are more efficient ways of programming this. I'm sorry.
  //   var cdata = this;
  //   var flangeLines = cdata.get('FlangeLine');
  //   var sightDots = cdata.get('SightDot');
  //   var topLines = cdata.get('TopLine');

  //   var flangeIndex = sandbox.flangeLineOptions.where({id: flangeLines.Id});
  //   var dotIndex = sandbox.sightDotOptions.where({id: sightDots.Id});
  //   var lineIndex = sandbox.topLineOptions.where({id: topLines.Id});

  //   var existingFlange = sandbox.putterOptions.get('existing_flange_line_parameters');
  //   var existingLines = sandbox.putterOptions.get('existing_top_line_parameters');

  //   var flangeParams, dotParams, lineParams;

  //   if(flangeIndex[0]){
  //     flangeParams = flangeIndex[0].get('parameter_keys').split(','); 
  //   }
  //   if(dotIndex[0]){
  //     dotParams = dotIndex[0].get('parameter_keys').split(',');
  //   }
  //   if(lineIndex[0]){
  //     lineParams = lineIndex[0].get('parameter_keys').split(',');
  //   }

  //   _.each(existingFlange, function(line){
  //     if(!flangeParams){flangeParams = [];}

  //     if(_.indexOf(flangeParams, line) === -1){
  //       flangeParams.push(line);
  //     }
  //   });

  //   _.each(existingLines, function(line){
  //     if(!lineParams){lineParams = [];}
  //     if(_.indexOf(lineParams, line) === -1){
  //       lineParams.push(line);
  //     }
  //   });

  //   _.each(flangeParams, function(line){
  //     this.variableOptionsMap[line] = true;
  //   }, this);
  //   _.each(dotParams, function(line){
  //     this.variableOptionsMap[line] = true;
  //   }, this);
  //   _.each(lineParams, function(line){
  //     this.variableOptionsMap[line] = true;
  //   }, this);
  // },